import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
} from '@tanstack/react-table';
import { Skeleton } from 'antd';
import { useEffect, useRef, useState } from 'react';

import {
  TableHeaderRows,
  TablePagination,
  useTableModel,
} from '@suntex.fim/ui';

const emptyStateElement = (
  <div className="table-empty-state">
    <h4>No matching records found</h4>
  </div>
);

function hasIsPrimaryTeam(value: unknown): value is { isPrimaryTeam: boolean } {
  return (
    typeof value === 'object' && value !== null && 'isPrimaryTeam' in value
  );
}

function hasIsLevelUp<T>(row: { original: T }) {
  const value = row.original as T & {
    activity1W?: number;
    activity2W?: number;
  };
  return (
    value.activity1W !== undefined &&
    value.activity2W !== undefined &&
    value.activity2W < value.activity1W
  );
}

export type AssessmentColumnType<T> = ColumnDef<T>;

export type AssessmentTableProps<T> = {
  data: T[];
  isLoading: boolean;
  globalFilter?: string;
  columns: AssessmentColumnType<T>[];
  manualSorting?: boolean;
  manualFiltering?: boolean;
  manualPagination?: boolean;
  totalEntries?: number;
  initialSorting?: SortingState;
  changeActions?: {
    onSortingChange?: OnChangeFn<SortingState>;
    onPaginationChange?: OnChangeFn<PaginationState>;
    onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  };
  manualState?: {
    sorting?: SortingState;
    pagination?: PaginationState;
    columnFilters?: ColumnFiltersState;
  };
  highLightColumnsById?: string[];
  highlightColumnsCell?: boolean;
  displayFooter?: boolean;
  footerValues?: (string | null)[];
  displayPagination?: boolean;
  hideFirstTwoColumns?: boolean;
  hideFirstColumns?: boolean;
  horizontalScroll?: boolean;
};

export function useAssessmentTable<T>({
  data,
  isLoading = true,
  globalFilter = '',
  columns,
  manualSorting = false,
  manualFiltering = false,
  manualPagination = false,
  manualState = {},
  changeActions = {},
  initialSorting = [],
  highLightColumnsById = [],
  highlightColumnsCell = false,
  displayFooter = false,
  footerValues = [],
  displayPagination = true,
  hideFirstTwoColumns = true,
  hideFirstColumns = true,
  horizontalScroll = false,
  ...props
}: AssessmentTableProps<T>) {
  const totalEntries = props.totalEntries ?? data.length;
  const tableRef = useRef<HTMLTableElement>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageSize: 25,
    pageIndex: 0,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    contactEmail: false,
    teamProgramBegins: false,
    teamProgramEnds: false,
  });
  const [rowSelection, setRowSelection] = useState({});
  const tableState = {
    columnFilters,
    columnVisibility,
    rowSelection,
    sorting,
    globalFilter,
    pagination,
    ...manualState,
  };
  const stateChanges = {
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
  };
  const table = useTableModel(
    data,
    columns,
    tableState,
    stateChanges,
    {
      manualFiltering,
      manualSorting,
      manualPagination,
    },
    changeActions,
  );

  useEffect(() => {
    if (displayPagination) {
      setPagination({ pageSize: 25, pageIndex: 0 });
    }
  }, [totalEntries, displayPagination]);

  function Table() {
    return (
      <>
        <div
          className={`assessment-table table-wrapper${horizontalScroll ? ' table-wrapper--horizontal-scroll' : ''}`}
          {...(horizontalScroll
            ? { role: 'region', 'aria-label': 'Scrollable table', tabIndex: 0 }
            : {})}
        >
          <table
            className="table"
            style={{
              minWidth: table.getTotalSize(),
              width: '100%',
              tableLayout: horizontalScroll ? 'fixed' : undefined,
            }}
            ref={tableRef}
          >
            {horizontalScroll ? (
              <colgroup>
                {table.getVisibleLeafColumns().map((column, index) => (
                  <col
                    key={column.id}
                    width={column.getSize()}
                    className={
                      (hideFirstTwoColumns && index < 2) ||
                      (!hideFirstTwoColumns && hideFirstColumns && index === 0)
                        ? 'print-hidden-column'
                        : undefined
                    }
                  />
                ))}
              </colgroup>
            ) : null}
            <thead
              className={`thead${hideFirstTwoColumns ? ' print-hidden-table' : hideFirstColumns ? ' print-pdf-first-column' : ''}`}
            >
              <TableHeaderRows
                table={table}
                highLightColumnsById={highLightColumnsById}
                widthProperty="maxWidth"
                sortIconClassName="print-hidden"
              />
            </thead>
            <tbody
              className={`tbody${hideFirstTwoColumns ? ' print-hidden-table' : hideFirstColumns ? ' print-pdf-first-column' : ''}`}
            >
              {isLoading
                ? [...Array(pagination.pageSize).keys()].map((index) => (
                    <tr className="tr" key={index}>
                      {table.getLeafHeaders().map((header) => (
                        <td
                          key={header.id}
                          className="td"
                          style={{
                            position: 'relative',
                            minWidth: header.getSize(),
                          }}
                        >
                          <Skeleton
                            loading
                            title={false}
                            paragraph={{ rows: 1 }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
              {table.getRowModel().rows.map((row) => (
                <tr
                  className={`tr ${
                    hasIsLevelUp(row) ? 'guest-player-wall-id' : ''
                  }`}
                  style={{ position: 'relative' }}
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      className={`td ${
                        highlightColumnsCell &&
                        highLightColumnsById.includes(cell.column.id)
                          ? 'highlight-cell'
                          : ''
                      } ${
                        cell.column.id === 'userId' &&
                        hasIsPrimaryTeam(cell.row.original) &&
                        !cell.row.original.isPrimaryTeam
                          ? 'guest-player-id'
                          : ''
                      }`}
                      style={{
                        position: 'relative',
                        minWidth: cell.column.getSize(),
                      }}
                      key={cell.id}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {displayFooter ? (
              <tfoot>
                <tr>
                  {footerValues.map((value, index) => {
                    const column = table.getVisibleLeafColumns()[index];
                    return <td key={column?.id ?? String(value)}>{value}</td>;
                  })}
                </tr>
              </tfoot>
            ) : null}
          </table>
          {!isLoading && !table.getRowModel().rows.length
            ? emptyStateElement
            : null}
        </div>

        {displayPagination ? (
          <TablePagination
            manualPagination={manualPagination}
            page={pagination.pageIndex + 1}
            pageSize={pagination.pageSize}
            totalEntries={
              manualPagination
                ? totalEntries
                : table.getFilteredRowModel().rows.length
            }
            totalRecord={table.getCoreRowModel().rows.length}
            isLoading={isLoading}
            setPage={(pageIndex) => {
              setPagination((previous) => ({
                ...previous,
                pageIndex: pageIndex - 1,
              }));
              if (manualPagination && changeActions.onPaginationChange) {
                changeActions.onPaginationChange((previous) => ({
                  ...previous,
                  pageIndex: pageIndex - 1,
                }));
              }
            }}
            setPageSize={(pageSize) => {
              setPagination((previous) => ({
                ...previous,
                pageIndex: 0,
                pageSize,
              }));
              if (manualPagination && changeActions.onPaginationChange) {
                changeActions.onPaginationChange((previous) => ({
                  ...previous,
                  pageIndex: 0,
                  pageSize,
                }));
              }
            }}
          />
        ) : null}
      </>
    );
  }

  return { table, Table, tableRef };
}
